<template>
    <section class="app-price-calculation-view container-fluid">
        <div class="app-view-header d-flex flex-wrap justify-content-center">
            <div class="app-view-header__headline-wrap d-flex align-items-center">
                <span class="app-view-header__headline-wrap-pseudo-helper"></span>
                <h2 class="app-view-header__headline">{{ $t('priceCalculation.headline') }}</h2>
                <span class="app-view-header__headline-wrap-pseudo-helper"></span>
            </div>
        </div>
        <div class="app-price-calculation-view__main">
            <div class="row">
                <div class="app-price-calculation-view__rental col-12 col-md-8 col-lg-7">
                    <div class="app-price-calculation-view__rental-inner">
                        <div class="app-price-calculation-view__rental-data row">
                            <div class="app-price-calculation-view__rental-data-col col-12" :class="numberOfProductsDataColClasses">
                                <div class="form-row align-items-start">
                                    <div class="app-price-calculation-view__number-of-products-col col form-group form-group--type-2">
                                        <label class="form-label" for="app-price-calculation-view__number-of-products">{{ $t('misc.quantity') }}</label>
                                        <input type="number"
                                            :value="numberOfProducts"
                                            @change="$store.commit('setProductQuantity', parseInt($event.target.value), 10)"
                                            min="1"
                                            max="99"
                                            class="app-price-calculation-view__number-of-products form-control"
                                            id="app-price-calculation-view__number-of-products">
                                    </div>
                                </div>
                            </div>
                            <div class="app-price-calculation-view__rental-data-col col">
                                <div class="form-row flex-sm-nowrap align-items-start">
                                    <div class="app-price-calculation-view__start-date-col col form-group form-group--type-2" :class="dateColClasses">
                                        <label class="form-label" for="app-price-calculation-view__start-date">{{ $t('priceCalculation.rentalStartDate') }}</label>
                                        <datepicker :value="startDate"
                                            class="input-group d-block app-price-calculation-view__start-date"
                                            @input="setDate('startDate', $event)"
                                            :format="formatDate"
                                            :full-month-name="true"
                                            :language="datepickerLanguage"
                                            :monday-first="true"
                                            :disabled-dates="disabledDatesOfStartDate"
                                            input-class="form-control app-price-calculation-view__start-date"
                                            id="app-price-calculation-view__start-date">
                                            <svg slot="afterDateInput" class="input-group__icon vdp-datepicker__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                <path d="M0 0h24v24H0z" fill="none"/>
                                                <path d="M19 3h-1V1h-2v2H8V1H6v2H5a2 2 0 00-1.99 2L3 19a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                                            </svg>
                                        </datepicker>
                                    </div>
                                    <div class="app-price-calculation-view__end-date-col col form-group form-group--type-2" :class="dateColClasses">
                                        <label class="form-label" for="app-price-calculation-view__end-date">{{ $t('priceCalculation.rentalEndDate') }}</label>
                                        <datepicker :value="endDate"
                                            class="input-group d-block app-price-calculation-view__end-date"
                                            @input="setDate('endDate', $event)"
                                            :format="formatDate"
                                            :full-month-name="true"
                                            :language="datepickerLanguage"
                                            :monday-first="true"
                                            :disabled-dates="disabledDatesOfEndDate"
                                            input-class="form-control app-price-calculation-view__end-date"
                                            id="app-price-calculation-view__end-date">
                                            <svg slot="afterDateInput" class="input-group__icon vdp-datepicker__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                                <path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5a2 2 0 00-1.99 2L3 19a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2h-1V1h-2zm3 18H5V8h14v11z"/>
                                                <path d="M0 0h24v24H0z" fill="none"/>
                                            </svg>
                                        </datepicker>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="app-price-calculation-view__total-price-box">
                            <div class="app-price-calculation-view__total-price row">
                                <b class="app-price-calculation-view__total-price-label col">{{ $t('priceCalculation.pricePerTruck') }}:</b>

                                <b class="app-price-calculation-view__total-price-tag col-auto">
                                    <template v-if="rentalPrice && rentalPriceRequestStatus === constants.requestStatuses.succeeded">
                                        {{ nEnhanced(rentalPrice, 'currency') }}
                                        <span v-if="priceFormat === constants.priceFormats.monthly" class="app-price-calculation-view__total-price-tag-suffix">{{ $t('misc.perMonth') }}</span>
                                    </template>
                                    <template v-else-if="rentalPriceRequestStatus === constants.requestStatuses.pending">
                                        <svg class="app-price-calculation-view__total-price-loader-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                                            <path opacity=".1" d="M14 0h4v8h-4z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0"/>
                                            </path>
                                            <path opacity=".1" d="M25.9 3.272l2.828 2.83-5.657 5.655-2.827-2.828z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.125s"/>
                                            </path>
                                            <path opacity=".1" d="M32 14v4h-8v-4z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.25s"/>
                                            </path>
                                            <path opacity=".1" d="M28.728 25.9l-2.83 2.828-5.655-5.657 2.828-2.827z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.375s"/>
                                            </path>
                                            <path opacity=".1" d="M18 32h-4v-8h4z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.5s"/>
                                            </path>
                                            <path opacity=".1" d="M6.1 28.728l-2.828-2.83 5.657-5.655 2.827 2.828z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.675s"/>
                                            </path>
                                            <path opacity=".1" d="M0 18v-4h8v4z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.75s"/>
                                            </path>
                                            <path opacity=".1" d="M3.272 6.1l2.83-2.828 5.655 5.657-2.828 2.827z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.875s"/>
                                            </path>
                                        </svg>
                                    </template>
                                    <template v-else>{{ $t('misc.priceOnRequest') }}</template>
                                </b>
                            </div>
                            <small v-if="pricePeriod === constants.pricePeriods.week" class="app-price-calculation-view__total-price-note d-block">
                                {{ $t('priceCalculation.pricePeriodNoteWeek') }}
                            </small>
                            <small v-else-if="pricePeriod === constants.pricePeriods.month" class="app-price-calculation-view__total-price-note d-block">
                                {{ $t('priceCalculation.pricePeriodNoteMonth') }}
                            </small>
                            <small v-if="tExists('priceCalculation.handlingPriceNote1')" class="app-price-calculation-view__total-price-note d-block">
                                {{ $t('priceCalculation.handlingPriceNote1') }}
                            </small>
                            <small v-if="tExists('priceCalculation.handlingPriceNote2')" class="app-price-calculation-view__total-price-note d-block">
                                {{ $t('priceCalculation.handlingPriceNote2') }}
                            </small>
                        </div>
                    </div>
                </div>
                <div class="app-price-calculation-view__transport col col-md-4 col-lg-5">
                    <div class="app-price-calculation-view__transport-inner">
                        <div class="app-price-calculation-view__transport-data form-row">
                            <div class="app-price-calculation-view__place-of-use-col col-auto form-group form-group--type-2">
                                <label class="form-label" for="app-price-calculation-view__place-of-use">{{ $t('priceCalculation.formPlaceOfUseLabel') }}</label>
                                <div class="input-group app-price-calculation-view__place-of-use-group">
                                    <input type="text"
                                        ref="placeOfUseInput"
                                        v-model="localPlaceOfUse"
                                        class="form-control app-price-calculation-view__place-of-use"
                                        id="app-price-calculation-view__place-of-use"
                                        :placeholder="$te('priceCalculation.postCode', this.$i18n.locale) ? $t('priceCalculation.postCode') : $t('personalData.postCode')"
                                    >
                                    <svg class="input-group__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36.35 52">
                                        <path class="path-outer" d="M31.03 5.32c-7.1-7.1-18.6-7.1-25.7 0-6.4 6.4-7.12 18.44-1.7 25.68L18.2 52l14.54-21c5.43-7.24 4.7-19.28-1.7-25.68zM18.35 24c-3.3 0-6-2.7-6-6s2.7-6 6-6a6 6 0 010 12z" fill="#f96915"/>
                                        <path class="path-inner" d="M17.66 24.05a6.23 6.23 0 01-3.75-1.88 5.78 5.78 0 01-1.6-3.9 5.67 5.67 0 01.94-3.56 6.3 6.3 0 013.6-2.62c.74-.2 2.3-.2 3.03 0 2.3.63 4.03 2.5 4.48 4.83.14.72.08 2.03-.1 2.7a6.1 6.1 0 01-3.32 3.9c-.72.34-1.18.47-1.96.56-.65.07-.65.07-1.3 0z" fill="#fff"/>
                                    </svg>
                                </div>
                            </div>
                        </div>
                        <div class="app-price-calculation-view__transport-price-box">
                            <div class="app-price-calculation-view__transport-price row">
                                <b class="app-price-calculation-view__transport-price-label col">{{ $t('priceCalculation.transportPrice') }}:</b>
                                <span class="app-price-calculation-view__transport-price-tag col-auto">
                                    <template v-if="transportPrice && transportPriceRequestStatus === constants.requestStatuses.succeeded">{{ nEnhanced(transportPrice, 'currency') }}</template>
                                    <template v-else-if="transportPriceRequestStatus === constants.requestStatuses.pending">
                                        <svg class="app-price-calculation-view__transport-price-loader-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                                            <path opacity=".1" d="M14 0h4v8h-4z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0"/>
                                            </path>
                                            <path opacity=".1" d="M25.9 3.272l2.828 2.83-5.657 5.655-2.827-2.828z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.125s"/>
                                            </path>
                                            <path opacity=".1" d="M32 14v4h-8v-4z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.25s"/>
                                            </path>
                                            <path opacity=".1" d="M28.728 25.9l-2.83 2.828-5.655-5.657 2.828-2.827z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.375s"/>
                                            </path>
                                            <path opacity=".1" d="M18 32h-4v-8h4z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.5s"/>
                                            </path>
                                            <path opacity=".1" d="M6.1 28.728l-2.828-2.83 5.657-5.655 2.827 2.828z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.675s"/>
                                            </path>
                                            <path opacity=".1" d="M0 18v-4h8v4z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.75s"/>
                                            </path>
                                            <path opacity=".1" d="M3.272 6.1l2.83-2.828 5.655 5.657-2.828 2.827z">
                                                <animate attributeName="opacity" from="1" to=".1" dur="1s" repeatCount="indefinite" begin="0.875s"/>
                                            </path>
                                        </svg>
                                    </template>
                                    <template v-else>{{ $t('misc.priceOnRequest') }}</template>
                                </span>
                            </div>
                            <small class="app-price-calculation-view__transport-price-note d-block" v-html="$t('priceCalculation.transportPriceNote')"></small>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="app-view-footer app-price-calculation-view__footer">
            <div class="app-view-footer__inner row flex-column flex-md-row justify-content-md-around align-items-center align-items-md-start">
                <div class="app-view-footer__col col">
                    <div class="app-view-footer__btn-wrap">
                        <button type="button" class="app-view-footer__btn btn btn--primary" :class="{ 'app-view-footer__btn--hidden': currentStep > 1 }" @click="goForward">{{ $t('priceCalculation.goForward') }} &downarrow;</button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { mapState } from 'vuex'
import Datepicker from 'vuejs-datepicker'
import store from '@/store'
import constants from '@/mixins/constants'
import addDays from '@/helpers/addDays'
import toRegExp from '@/helpers/toRegExp'
import { debounce } from 'lodash'

const datepickerLocale = process.env.VUE_APP_I18N_LOCALE.slice(0, 2)
const initialNumberOfProducts = 1
const today = new Date()
const initialStartDate = addDays(today, 2)
const initialEndDate = addDays(today, 31)
const placeOfUseRegEx = toRegExp(process.env.VUE_APP_PLACE_OF_USE_REG_EX)
const isIE11 = !!window.MSInputMethodContext && !!document.documentMode

export default {
    name: 'PriceCalculation',
    components: {
        Datepicker
    },
    mixins: [constants],
    data: () => ({
        // helper to avoid update chain if the value is invalid after input
        localPlaceOfUse: store.state.placeOfUse === null ? '' : store.state.placeOfUse,
        // BEWARE: As we cannot surround with forward slashes and use flags
        // in pattern attribute of input element we must check validity programmatically
        placeOfUseRegEx: placeOfUseRegEx || /^.+$/i,
        datepickerLanguage: undefined
    }),
    computed: {
        ...mapState([
            'productPathSegment',
            'startDate',
            'endDate',
            'rentalPrice',
            'rentalPriceRequestStatus',
            'pricePeriod',
            'priceFormat',
            'transportPrice',
            'transportPriceRequestStatus',
            'currentStep'
        ]),
        product: (vm) => vm.$store.getters.product,
        numberOfProducts: (vm) => vm.$store.state.productQuantity,
        disabledDatesOfStartDate: () => {
            return {
                // BEWARE: exclusive and outward range
                to: addDays(initialStartDate, -1)
            }
        },
        disabledDatesOfEndDate: (vm) => {
            return {
                // BEWARE: exclusive and outward range
                to: vm.startDate
            }
        },
        // Unfortunately this is necessary to fix the flex behavior in IE 11
        // Now the 3 fields are supposed to stretch and both date fields to be on a new line
        numberOfProductsDataColClasses: () => ({
            'col-sm-auto': !isIE11
        }),
        dateColClasses: () => ({
            'flex-sm-grow-0': !isIE11
        })
    },
    watch: {
        localPlaceOfUse (newValue) {
            this.updateTransportPrice(newValue)
        }
    },
    methods: {
        formatDate (date) {
            return this.$d(date, 'short')
        },
        setDate (prop, date) {
            if (Object.prototype.toString.call(date) !== '[object Date]' || !(date instanceof Date)) {
                this.$store.commit('reset', prop)
            } else {
                this.$store.commit(prop === 'startDate' ? 'setStartDate' : 'setEndDate', date)
            }
            this.$store.dispatch('updateRentalPrice')
        },
        updateTransportPrice (placeOfUse) {
            if (typeof placeOfUse !== 'string' || !placeOfUse.length || !this.placeOfUseRegEx.test(placeOfUse)) {
                this.$store.dispatch('updateTransportPrice', { placeOfUse: null })
            } else {
                this.$store.dispatch('updateTransportPrice', { placeOfUse: placeOfUse })
            }
        },
        goForward () {
            if (this.$store.state.productQuantity === null) {
                this.$store.commit('setProductQuantity', this.numberOfProducts)
            }
            if (this.$store.state.startDate === null) {
                this.$store.commit('setStartDate', this.startDate)
            }
            if (this.$store.state.endDate === null) {
                this.$store.commit('setEndDate', this.endDate)
            }
            if (this.currentStep < 2) {
                this.$store.commit('setCurrentStep', 2)
            }

            this.$router.push({
                name: 'steps',
                hash: '#product-options',
                params: {
                    productPathSegment: this.productPathSegment
                }
            })
        }
    },
    created () {
        import(`vuejs-datepicker/dist/locale/translations/${datepickerLocale}.js`)
            .then(({ default: lang }) => {
                this.datepickerLanguage = lang
            })

        if (this.$store.state.productQuantity === null) this.$store.commit('setProductQuantity', initialNumberOfProducts)
        if (this.$store.state.startDate === null) this.$store.commit('setStartDate', initialStartDate)
        if (this.$store.state.endDate === null) this.$store.commit('setEndDate', initialEndDate)
        this.$store.dispatch('updateRentalPrice')

        const debouncedUpdateTransportPrice = debounce(this.updateTransportPrice, 500)
        this.$watch('numberOfProducts', () => {
            debouncedUpdateTransportPrice(this.localPlaceOfUse)
        })
    }
}
</script>

<style scoped>
    .app-price-calculation-view {
        position: relative;
        max-width: 1200px;
        padding-bottom: 20px;
    }
    .app-price-calculation-view__main {
        margin-bottom: 2em;
        background-color: #fff;
    }
    .app-price-calculation-view__rental {
        margin-bottom: 20px;
    }
    .app-price-calculation-view__rental-inner,
    .app-price-calculation-view__transport-inner {
        padding: 15px;
    }
    .app-price-calculation-view__rental-data,
    .app-price-calculation-view__transport-data {
        margin-bottom: 2em;
    }
    .app-price-calculation-view__rental-data {
        margin-top: -15px;
        margin-left: -5px;
        margin-right: -5px;
    }
    .app-price-calculation-view__rental-data-col {
        padding-top: 15px;
        padding-left: 5px;
        padding-right: 5px;
    }
    .app-price-calculation-view__rental-data .form-label {
        white-space: nowrap;
        padding-right: .5em;
    }
    .app-price-calculation-view__number-of-products {
        min-width: 70px;
        /* max-width required for firefox */
        max-width: 200px;
    }
    .app-price-calculation-view__start-date,
    .app-price-calculation-view__end-date {
        min-width: 170px;
        max-width: 250px;
    }
    /* https://vue-loader.vuejs.org/guide/scoped-css.html#deep-selectors */
    .app-price-calculation-view__start-date-col >>> input,
    .app-price-calculation-view__end-date-col >>> input {
        max-width: 100%;
        /* regular padding-right + width of svg icon + offset right */
        padding-right: calc(15px + 20px + 15px);
    }
    .app-price-calculation-view__total-price,
    .app-price-calculation-view__transport-price {
        margin-top: -10px;
        margin-bottom: 15px;
    }
    .app-price-calculation-view__total-price-label,
    .app-price-calculation-view__total-price-tag {
        padding-top: 10px;
        font-size: 2em;
    }
    .app-price-calculation-view__total-price-label {
        color: #545454;
    }
    .app-price-calculation-view__total-price-tag,
    .app-price-calculation-view__transport-price-tag {
        color: #f96915;
    }
    .app-price-calculation-view__total-price-note,
    .app-price-calculation-view__transport-price-note {
        font-size: .8em;
        color: #969492;
    }
    .app-price-calculation-view__transport {
        position: relative;
        padding-top: 20px;
    }
    .app-price-calculation-view__transport:before {
        content: '';
        position: absolute;
        left: 15px;
        top: 0;
        right: 15px;
        height: 1px;
        background-color: #cbc9c8;
    }
    .app-price-calculation-view__place-of-use-group {
        width: 250px;
        max-width: 100%;
    }
    .app-price-calculation-view__transport-price-label,
    .app-price-calculation-view__transport-price-tag {
        padding-top: 10px;
    }
    .app-price-calculation-view__transport-price-tag {
        font-weight: 700;
    }
    .app-price-calculation-view__total-price-loader-icon,
    .app-price-calculation-view__transport-price-loader-icon {
        width: 18px;
        height: 18px;
    }
    .app-price-calculation-view__place-of-use {
        /* regular padding-right + width of svg icon + offset right */
        padding-right: calc(.9em + 20px + 15px);
    }

    @media ( min-width: 768px ) {
        .app-price-calculation-view__rental {
            margin-bottom: 0;
        }
        .app-price-calculation-view__rental-inner,
        .app-price-calculation-view__transport-inner {
            padding: 30px;
        }
        .app-price-calculation-view__transport {
            padding-top: 0;
        }
        .app-price-calculation-view__transport:before {
            left: 0;
            right: auto;
            bottom: 0;
            width: 1px;
            height: auto;
        }
    }

    @media ( min-width: 992px ) {
        .app-price-calculation-view__rental-inner,
        .app-price-calculation-view__transport-inner {
            padding: 50px;
        }
    }
</style>

<style>
    /* Datepicker - cannot use scoped styles */
    .app .vdp-datepicker__calendar,
    .app .vdp-datepicker__calendar .cell,
    .app .vdp-datepicker__calendar header span {
        border-radius: 2px;
    }
    .app .vdp-datepicker__calendar .cell.day-header {
        cursor: default;
    }
    .app .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
    .app .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover,
    .app .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
        border-color: transparent;
        background-color: #f2f2f2;
    }
    .app .vdp-datepicker__calendar .cell.selected.selected.selected.selected {
        color: #fff;
        background-color: #f96915;
    }
</style>
